import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { GitMerge, Pin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ontologyGroupStyles, type OntologyGroup } from '@/frontend/features/Fde/ontologyGraphGroups'

export interface OntologyNodeData extends Record<string, unknown> {
  name: string
  className: string
  imageUrl: string | null
  subtitle: string
  group: OntologyGroup
  connectionCount: number
  status: 'proposto' | 'confirmado'
  active: boolean
  anchor: boolean
  shared: boolean
  dimmed: boolean
  orientation: 'horizontal' | 'vertical'
  relationshipLabel: string
  anchorLabel: string
  sharedLabel: string
}

export type OntologyFlowNode = Node<OntologyNodeData, 'ontology'>

export default function OntologyGraphNode({ data }: NodeProps<OntologyFlowNode>) {
  const colors = ontologyGroupStyles[data.group]
  const targetPosition = data.orientation === 'vertical' ? Position.Top : Position.Left
  const sourcePosition = data.orientation === 'vertical' ? Position.Bottom : Position.Right
  return (
    <div className={cn(
      'relative flex size-full overflow-hidden rounded-xl border bg-card shadow-sm transition-[opacity,border-color,box-shadow] duration-200',
      data.status === 'proposto' && 'border-dashed',
      data.active || data.anchor ? `ring-4 ${colors.selected}` : 'border-border',
      data.dimmed && 'opacity-20',
    )} data-ontology-anchor={data.anchor ? 'true' : 'false'}>
      <Handle type="target" position={targetPosition} className="!size-1 !border-0 !bg-transparent !opacity-0" />
      <span className={cn('w-1 shrink-0', colors.accent)} />
      <div className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {data.imageUrl ? <img src={data.imageUrl} alt="" className="size-6 shrink-0 rounded-lg border border-border object-cover" /> : <span className={cn('size-2 shrink-0 rounded-full', colors.dot)} />}
          <p className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground" title={data.name}>{data.name}</p>
          {data.anchor && <Pin className="size-3 shrink-0 text-primary" aria-label={data.anchorLabel} />}
          {!data.anchor && data.shared && <GitMerge className="size-3 shrink-0 text-primary" aria-label={data.sharedLabel} />}
        </div>
        {data.subtitle && <p className="mt-1 truncate text-[10px] font-medium text-foreground/80">{data.subtitle}</p>}
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
          <span className="truncate">{data.className}</span>
          <span className="shrink-0 tabular-nums">{data.connectionCount} {data.relationshipLabel}</span>
        </div>
      </div>
      <Handle type="source" position={sourcePosition} className="!size-1 !border-0 !bg-transparent !opacity-0" />
    </div>
  )
}
